import React from "react";

import {
  Alert,
  Card,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import { EmailPreferences, User } from "../../../types";

import { useUpdateCurrentUser } from "../../../store";

interface EmailPreferencesCardProps {
  user: User;
}

const EmailPreferencesCard: React.FC<EmailPreferencesCardProps> = ({
  user,
}) => {
  const [updateInProgress, setUpdateInProgress] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [emailPreferences, setEmailPreferences] =
    React.useState<EmailPreferences>(
      user.emailPreferences || EmailPreferences.All,
    );

  const onSuccess = () => {
    setSnackbar({
      open: true,
      message: "Details updated successfully",
      severity: "success",
    });

    setUpdateInProgress(false);
  };

  const onError = (error: unknown) => {
    console.log(error);
    setSnackbar({
      open: true,
      message: "Failed to update details",
      severity: "error",
    });

    if (user.emailPreferences) {
      setEmailPreferences(user.emailPreferences);
    }

    setUpdateInProgress(false);
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const { mutate: updateUser } = useUpdateCurrentUser(onSuccess, onError);

  const onInvitationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const newPreferences = checked
      ? EmailPreferences.All
      : EmailPreferences.OnlyJoin;

    if (newPreferences == emailPreferences) {
      return;
    }

    setUpdateInProgress(true);

    updateUser({
      emailPreferences: newPreferences,
    });

    setEmailPreferences(newPreferences);
  };

  return (
    <Card>
      <Stack direction={"column"} justifyContent={"space-between"} padding={2}>
        <Typography variant="h4">Email Preferences</Typography>
        <Tooltip
          title={"Receive email notifications for changes to meetings?"}
          placement="right"
          enterDelay={1000}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              disabled={updateInProgress}
              checked={emailPreferences === EmailPreferences.All}
              onChange={onInvitationChange}
            />
            <Typography variant="body1">Meeting Notifications</Typography>
          </Stack>
        </Tooltip>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EmailPreferencesCard;
